import { z } from "zod";

// Folders mirror media.folder enum in packages/db/src/schema/media.ts
export const MEDIA_FOLDERS = [
  "destinations",
  "trips",
  "activities",
  "guides",
  "blog",
  "teams",
  "general",
  "departure-photos",
] as const;

export const MediaFolderSchema = z.enum(MEDIA_FOLDERS);
export type MediaFolder = z.infer<typeof MediaFolderSchema>;

/**
 * Media as returned by the API. `url`/`thumbUrl` are derived server-side
 * (CDN + cdn-cgi/image transform), not stored in the DB.
 */
export const MediaSchema = z.object({
  id: z.string(),
  r2Key: z.string(),
  r2KeyThumb: z.string(),
  r2KeyOriginal: z.string().nullable(),
  alt: z.string(),
  title: z.string().nullable(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  sizeBytes: z.number().int().nonnegative(),
  mimeType: z.string(),
  originalName: z.string().nullable(),
  folder: MediaFolderSchema,
  focalPointX: z.number().min(0).max(1),
  focalPointY: z.number().min(0).max(1),
  blurDataUrl: z.string().nullable(),
  tags: z.array(z.string()),
  description: z.string().nullable().optional(),
  uploadedAt: z.string(),
  uploadedBy: z.string().nullable(),
  deletedAt: z.string().nullable().optional(),
  // Derived (built API-side):
  url: z.string(),
  thumbUrl: z.string(),
});

export const MediaCreateSchema = z.object({
  alt: z.string().min(1, "Alt text is required for SEO + accessibility").max(125),
  title: z.string().max(200).optional(),
  folder: MediaFolderSchema,
  focalPointX: z.number().min(0).max(1).default(0.5),
  focalPointY: z.number().min(0).max(1).default(0.5),
  tags: z.array(z.string()).default([]),
  description: z.string().max(500).nullable().optional(),
});

export const MediaUpdateSchema = MediaCreateSchema.partial();

export type MediaResponse = z.infer<typeof MediaSchema>;
export type MediaCreate = z.infer<typeof MediaCreateSchema>;
export type MediaUpdate = z.infer<typeof MediaUpdateSchema>;

/** AI metadata suggestion result (POST /api/admin/upload/suggest-alt). */
export interface AltSuggestion {
  alt: string;
  title: string;
  tags: string[];
  description: string;
}
